/**
 * Utilities for extracting job field values from boundary GeoJSON properties
 * using property mappings established in the wizard's Property Mapping step.
 */
import type { Feature, FeatureCollection } from "geojson";
import type { Job } from "../../../models";
import type { PropertyMappings } from "./PropertyMappings.types";

/**
 * Extract a property value from a feature using an array of property names (priority order).
 * Returns the first non-empty string value found.
 */
export function extractPropertyValue(feature: Feature, propertyNames: string[] | null | undefined): string {
	if (!propertyNames || propertyNames.length === 0 || !feature.properties) return "";
	for (const propName of propertyNames) {
		const value = feature.properties[propName];
		if (value !== null && value !== undefined && String(value).trim() !== "") {
			return String(value);
		}
	}
	return "";
}

/**
 * Merge all group property mappings into a single effective set.
 * For each field, takes the first non-null array found across all groups.
 */
export function mergePropertyMappings(mappingsRecord: Record<string, PropertyMappings>): PropertyMappings {
	const result: Partial<PropertyMappings> = {};
	const allKeys = new Set<keyof PropertyMappings>();

	// Collect all possible keys from all group mappings
	for (const mappings of Object.values(mappingsRecord)) {
		for (const key of Object.keys(mappings) as (keyof PropertyMappings)[]) {
			allKeys.add(key);
		}
	}

	// For each key, take the first non-null value
	for (const key of allKeys) {
		for (const mappings of Object.values(mappingsRecord)) {
			if (mappings[key] != null) {
				(result as any)[key] = mappings[key];
				break;
			}
		}
	}

	return result as PropertyMappings;
}

/**
 * Field mapping from PropertyMappings keys to Job property names.
 * Some mappings set multiple Job fields (e.g. grower → growerName + importGrowerName)
 * to match the behavior of defaultJobForImport.
 * Excludes fields set by deal/branch/salesperson assignment steps.
 */
const MAPPING_TO_JOB_FIELDS: Partial<Record<keyof PropertyMappings, (keyof Job)[]>> = {
	field: ["fieldName", "importFieldName"],
	farm: ["farmName", "importFarmName"],
	grower: ["growerName", "importGrowerName"],
	fieldId: ["fieldId"],
	labName: ["importLabName"],
	labInstructions: ["labInstructions"],
	submissionNotes: ["submissionNotes"],
	yieldGoal: ["yieldGoal"],
	submitterName: ["submitterName"],
	submitterEmail: ["submitterEmail"],
	growerPhoneNumber: ["growerPhoneNumber"],
	eventId: ["eventId"],
	labSubmissionCode: ["labSubmissionCode"],
	externalId: ["externalId"],
	externalFarmId: ["externalFarmId"],
	externalLabId: ["externalLabId"],
};

/**
 * Extract job field values from a boundary GeoJSON's feature properties
 * using the property mappings from the wizard configuration.
 *
 * Uses the first feature in the FeatureCollection (consistent with
 * extractPropertyValueFromMatch in createJobsFromWizard.ts).
 *
 * Does NOT extract: density, depth, frequencyOfMicros, testPackage,
 * branch, salesperson, salespersonMobilePhone (set by other wizard steps).
 */
export function extractJobFieldsFromBoundary(
	boundaryGeoJSON: FeatureCollection,
	propertyMappingsRecord: Record<string, PropertyMappings>,
): Partial<Job> {
	if (!boundaryGeoJSON?.features?.length) return {};

	const mappings = mergePropertyMappings(propertyMappingsRecord);
	if (!mappings || Object.keys(mappings).length === 0) return {};

	const feature = boundaryGeoJSON.features[0];
	const result: Partial<Job> = {};

	for (const [mappingKey, jobFields] of Object.entries(MAPPING_TO_JOB_FIELDS)) {
		const propertyNames = mappings[mappingKey as keyof PropertyMappings];
		const value = extractPropertyValue(feature, propertyNames);
		if (value) {
			for (const jobField of jobFields!) {
				(result as any)[jobField] = value;
			}
		}
	}

	return result;
}
